import { freeSubscription } from "API/ScannerAPI";
import React from "react";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import moment from "moment";
import ConfirmAction from "Layout/Components/ConfirmAction";
import { cancelPaymentSubscription, getPaymentList, SUBSCRIPTION_TYPES } from "API/StripeAPI";

class ScannerApiSubscriptions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchTerm: "",
      subscriptions: [],
      currentPage: 1,
      pageSize: 10,
      loading: false,
    };
  }

  componentDidMount() {
    this.fetchSubscriptions();
  }

  async fetchSubscriptions() {
    this.setState({ loading: true });
    try {
      // 1) Get subscription data from API
      let response = await getPaymentList(SUBSCRIPTION_TYPES.VALID);
      let rawSubs = response.data || [];

      // 2) If empty => create free subscription => re-fetch
      if (rawSubs.length === 0) {
        await freeSubscription();
        response = await getPaymentList(SUBSCRIPTION_TYPES.VALID);
        rawSubs = response.data || [];
      }

      // 3) Transform the data to match your table shape
      const subscriptions = rawSubs.map((item) => ({
        invoiceId: item.id,
        description: item.plantype || "",
        credits: item.credits || 0,
        renewalDate: item.paymentexpiredate,
        price: item.amount_total || 0,
        status: item.status || "",
        subscription: item.subscription || "",
      }));

      this.setState({ subscriptions });
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      this.setState({ loading: false });
    }
  }

  async handleCancelSubscription(subscriptionId) {
    try {
      this.setState({ loading: true, error: null });
      response = await cancelPaymentSubscription(subscriptionId)
      await this.fetchSubscriptions();
    } catch (error) {
      console.error("Error canceling subscription:", error);
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  }

  handleSearch = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  nextPage = () => {
    this.setState((prevState) => ({
      currentPage: prevState.currentPage + 1,
    }));
  };

  prevPage = () => {
    this.setState((prevState) => ({
      currentPage: Math.max(prevState.currentPage - 1, 1),
    }));
  };

  render() {
    const { t } = this.props;
    const { subscriptions, searchTerm, currentPage, pageSize, loading } =
      this.state;

    // Filter subscriptions by search term
    const filteredSubscriptions = subscriptions.filter(
      (item) =>
        (item.invoiceId && item.invoiceId.includes(searchTerm)) ||
        (item.description &&
          item.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const totalFiltered = filteredSubscriptions.length;
    const startIndex = (currentPage - 1) * pageSize;
    const pageData = filteredSubscriptions.slice(
      startIndex,
      startIndex + pageSize
    );

    const validSubscription = (sub) => {
      return sub.description && sub.description.includes("subscription") && moment(sub.renewalDate).isAfter(moment());
    }

    const cancelSubscriptionAction = (sub) => {
      if (!validSubscription(sub)) {
        return <span className="text-muted"></span>;
      }
      return <ConfirmAction btnClassName="mb-2 mr-2" color="danger" title={t('actions.confirmation')}
        message={t("messages.confirm_cancel", { object: sub.invoiceId })}
        onConfirm={() => this.handleCancelSubscription(sub.subscription)}>
          <span className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
            <i className="pe-7s-trash" />
            {t("phish_guard_url_checker.subscriptions.cancel")}
          </span>
      </ConfirmAction>;
    };

    return (
      <div className="scanner-subscriptions-page">
        <h2 className="page-heading">{t("phish_guard_url_checker.subscriptions.title")}</h2>
        <div className="subscriptions-banner">
          <span className="banner-text">
            {t("phish_guard_url_checker.subscriptions.banner_text")}
          </span>
          <div className="banner-buttons">
            <Link to="/organization/phish_guard_url_checker/plans">
              <Button color="primary" className="mr-2">
                {t("phish_guard_url_checker.subscriptions.banner_buy_more")}
              </Button>
            </Link>
          </div>
        </div>
        <Card>
          <CardBody>
            {loading ? (
              <div style={{ textAlign: "center", margin: "2rem 0" }}>
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                <Row className="mb-2">
                  <Col md="6">
                    <strong>
                      {t("phish_guard_url_checker.subscriptions.table.invoices")}
                    </strong>
                  </Col>
                  <Col md="6" className="text-right">
                    <Input
                      type="text"
                      value={searchTerm}
                      onChange={this.handleSearch}
                      placeholder={t("phish_guard_url_checker.subscriptions.table.search")}
                      style={{ display: "inline-block", width: "200px" }}
                    />
                  </Col>
                </Row>
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>{t("phish_guard_url_checker.subscriptions.table.invoices")}</th>
                      <th>{t("phish_guard_url_checker.subscriptions.table.description")}</th>
                      <th>{t("phish_guard_url_checker.subscriptions.table.credits")}</th>
                      <th>{t("phish_guard_url_checker.subscriptions.table.next_renewal_date")}</th>
                      <th>{t("phish_guard_url_checker.subscriptions.table.price")}</th>
                      <th>{t("phish_guard_url_checker.subscriptions.table.action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((sub, idx) => (
                      <tr key={idx}>
                        <td>{sub.invoiceId}</td>
                        <td>{t(`phish_guard_url_checker.subscriptions.${sub.description}`)}</td>
                        <td className="text-right">{sub.credits}</td>
                        <td className="text-right">{moment(sub.renewalDate).local().format("YYYY-MM-DD")}</td>
                        <td className="text-right">{'$ ' + Number(sub.price).toFixed(2)}</td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            {cancelSubscriptionAction(sub)}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {pageData.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center">
                          {t("phish_guard_url_checker.subscriptions.table.no_results")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    {t("phish_guard_url_checker.subscriptions.table.showing_entries", {
                      from: startIndex + 1,
                      to: startIndex + pageData.length,
                      total: totalFiltered,
                    })}
                  </span>
                  <div>
                    <Button
                      color="light"
                      size="sm"
                      onClick={this.prevPage}
                      disabled={this.state.currentPage <= 1}
                      className="mr-1"
                    >
                      {t("phish_guard_url_checker.subscriptions.table.previous")}
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      onClick={this.nextPage}
                      disabled={
                        this.state.currentPage >=
                        Math.ceil(totalFiltered / pageSize)
                      }
                    >
                      {t("phish_guard_url_checker.subscriptions.table.next")}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    );
  }
}

export default withTranslation()(ScannerApiSubscriptions);
